// Redes sociais — ver docs/API.md do marketingbackgo, seção "Redes sociais".

export type Network = 'instagram' | 'facebook' | 'pinterest' | 'tiktok' | 'youtube';

export const NETWORKS: Network[] = ['instagram', 'facebook', 'pinterest', 'tiktok', 'youtube'];

export const NETWORK_LABELS: Record<Network, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  pinterest: 'Pinterest',
  tiktok: 'TikTok',
  youtube: 'YouTube',
};

export type PostFormat =
  | 'ig_feed'
  | 'ig_carousel'
  | 'ig_reel'
  | 'ig_story'
  | 'fb_post'
  | 'fb_reel'
  | 'pin_image'
  | 'pin_video'
  | 'tiktok'
  | 'yt_short';

export type PostStatus =
  | 'strategy'
  | 'draft'
  | 'awaiting_approval'
  | 'changes_requested'
  | 'approved'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'partially_published'
  | 'failed';

export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  strategy: 'Bloco de estratégia',
  draft: 'Rascunho',
  awaiting_approval: 'Aguardando aprovação',
  changes_requested: 'Ajustes solicitados',
  approved: 'Aprovado',
  scheduled: 'Agendado',
  publishing: 'Publicando',
  published: 'Publicado',
  partially_published: 'Publicado em parte',
  failed: 'Falhou',
};

export type AspectRatio = '1:1' | '4:5' | '9:16' | '2:3' | '16:9' | 'original';

/** Regra de cada formato (GET /social/formats — fonte: domain.FormatRules). */
export interface FormatRule {
  format: PostFormat;
  network: Network;
  label: string;
  min_media: number;
  max_media: number;
  kinds: ('image' | 'video')[];
  ratios: AspectRatio[];
  default_ratio: AspectRatio;
  max_caption: number;
  max_hashtags: number;
  min_seconds?: number;
  max_seconds?: number;
  needs_title: boolean;
  max_title?: number;
  needs_board: boolean;
  wants_link: boolean;
  needs_privacy: boolean;
  privacy?: string[];
  first_comment: boolean;
  in_grid: boolean;
  /** Aceita imagem da Biblioteca como capa do vídeo (Reels do Instagram). */
  cover_image?: boolean;
  /** Aceita collab: até MAX_COLLABORATORS perfis convidados como coautores (Instagram). */
  collaborators?: boolean;
}

export interface FormatsResponse {
  formats: FormatRule[];
  networks: Network[];
  network_labels: Record<Network, string>;
  status_labels: Record<PostStatus, string>;
}

export interface MediaBrief {
  id: number;
  kind: 'image' | 'video';
  title: string;
  status: string;
  width?: number;
  height?: number;
  duration_seconds?: number;
  size_bytes: number;
  ai_description?: string;
  thumb_url?: string;
  preview_url?: string;
  deleted: boolean;
  /** Veio da Biblioteca de produção (editor de e-mail em HML). */
  fromProduction?: boolean;
}

export interface PostMedia {
  id?: number;
  target_id?: number;
  media_id: number;
  sort_order?: number;
  aspect_ratio?: AspectRatio;
  focal_x?: number;
  focal_y?: number;
  alt_text?: string;
  cover_at_seconds?: number;
  /** Imagem da Biblioteca usada como capa do vídeo (só Reels do Instagram). */
  cover_media_id?: number;
  media?: MediaBrief;
  cover?: MediaBrief;
}

export interface PostTarget {
  id: number;
  post_id: number;
  account_id?: number;
  account_name?: string;
  network: Network;
  format: PostFormat;
  caption?: string;
  title?: string;
  link_url?: string;
  board_name?: string;
  privacy?: string;
  options_json?: string;
  status: 'pending' | 'publishing' | 'published' | 'failed';
  external_id?: string;
  permalink?: string;
  error_message?: string;
  attempts: number;
  published_at?: string;
  /** auto = o sistema publica; reminder = lembrete (publica pelo app e marca como publicado). */
  mode: 'auto' | 'reminder';
}

export type CommentKind = 'comment' | 'submitted' | 'approved' | 'changes_requested' | 'withdrawn' | 'published' | 'reopened';

export const COMMENT_KIND_LABELS: Record<CommentKind, string> = {
  comment: 'comentou',
  submitted: 'enviou para aprovação',
  approved: 'aprovou',
  changes_requested: 'pediu ajustes',
  withdrawn: 'voltou para rascunho',
  published: 'marcou como publicado',
  reopened: 'voltou para aprovação',
};

export interface PostComment {
  id: number;
  post_id: number;
  kind: CommentKind;
  body?: string;
  author_email: string;
  author_name?: string;
  created_at: string;
}

export interface PostIssue {
  level: 'error' | 'warning';
  target_id?: number;
  format?: PostFormat;
  message: string;
}

export interface SocialPost {
  id: number;
  grid_hidden_at?: string;
  grid_order?: number;
  title: string;
  caption?: string;
  first_comment?: string;
  notes?: string;
  pillar_id?: number;
  pillar_name?: string;
  pillar_color?: string;
  status: PostStatus;
  scheduled_at?: string;
  owner_member_id?: number;
  owner_name?: string;
  activity_id?: number;
  link_url?: string;
  show_in_link_in_bio: boolean;
  submitted_at?: string;
  submitted_by?: string;
  approved_at?: string;
  approved_by?: string;
  published_at?: string;
  reminder_sent_at?: string;
  targets: PostTarget[];
  media: PostMedia[];
  comments?: PostComment[];
  issues: PostIssue[];
  can_edit: boolean;
  can_approve: boolean;
  created_at: string;
  created_by: string;
  updated_at?: string;
  last_updated_by?: string;
}

export interface SaveMediaInput {
  media_id: number;
  aspect_ratio?: AspectRatio | null;
  focal_x?: number | null;
  focal_y?: number | null;
  alt_text?: string | null;
  cover_at_seconds?: number | null;
  cover_media_id?: number | null;
}

export interface SaveTargetInput {
  id?: number | null;
  account_id?: number | null;
  format: PostFormat;
  caption?: string | null;
  title?: string | null;
  link_url?: string | null;
  board_name?: string | null;
  privacy?: string | null;
  options_json?: string | null;
  media: SaveMediaInput[];
}

export interface SavePostRequest {
  title: string;
  caption?: string | null;
  first_comment?: string | null;
  notes?: string | null;
  pillar_id?: number | null;
  scheduled_at?: string | null;
  owner_member_id?: number | null;
  link_url?: string | null;
  show_in_link_in_bio: boolean;
  strategy: boolean;
  media: SaveMediaInput[];
  targets: SaveTargetInput[];
}

export interface PostFilter {
  from?: string;
  to?: string;
  status?: PostStatus[];
  network?: Network | '';
  account_id?: number;
  pillar_id?: number;
  media_id?: number;
  search?: string;
  scheduled?: boolean;
  unscheduled?: boolean;
}

export interface SocialAccount {
  id: number;
  network: Network;
  username: string;
  display_name?: string;
  external_id?: string;
  avatar_url?: string;
  profile_url?: string;
  access_token?: string;
  refresh_token?: string;
  token_expires_at?: string;
  scopes?: string;
  params_json?: string;
  publish_mode: 'reminder' | 'auto';
  status: 'manual' | 'connected' | 'expired' | 'error';
  status_message?: string;
  is_active: boolean;
  /** Perfil público lido da rede (Ler feed / a cada 6 h). */
  biography?: string;
  website?: string;
  followers_count?: number;
  follows_count?: number;
  media_count?: number;
  profile_synced_at?: string;
  created_at: string;
  created_by: string;
  updated_at?: string;
  last_updated_by?: string;
}

export type ConnectionLevel = 'ok' | 'warning' | 'error';

export interface ConnectionReportItem {
  level: ConnectionLevel;
  kind: 'connected' | 'page_without_ig' | 'page_no_publish' | 'missing_scope' | 'not_returned' | 'no_pages';
  network?: Network;
  username?: string;
  page_name?: string;
  title: string;
  detail?: string;
  fix?: string;
  new?: boolean;
}

/** Diagnóstico de um login (Contas conectadas → Conectar). */
export interface ConnectionReport {
  id: number;
  network: OAuthNetwork;
  created_at?: string;
  created_by?: string;
  connected: number;
  warnings: number;
  errors: number;
  items: ConnectionReportItem[];
}

export interface SaveAccountRequest {
  network: Network;
  username: string;
  display_name?: string | null;
  external_id?: string | null;
  avatar_url?: string | null;
  profile_url?: string | null;
  access_token?: string | null;
  refresh_token?: string | null;
  token_expires_at?: string | null;
  scopes?: string | null;
  params_json?: string | null;
  publish_mode: 'reminder' | 'auto';
  is_active: boolean;
}

/** Rota de conexão (OAuth) de cada rede. Instagram e Facebook conectam juntos (Meta). */
export type OAuthNetwork = 'meta' | 'pinterest' | 'tiktok' | 'youtube';

export const OAUTH_NETWORKS: { key: OAuthNetwork; label: string; networks: Network[] }[] = [
  { key: 'meta', label: 'Instagram e Facebook', networks: ['instagram', 'facebook'] },
  { key: 'pinterest', label: 'Pinterest', networks: ['pinterest'] },
  { key: 'tiktok', label: 'TikTok', networks: ['tiktok'] },
  { key: 'youtube', label: 'YouTube', networks: ['youtube'] },
];

export const TARGET_STATUS_LABELS: Record<PostTarget['status'], string> = {
  pending: 'Pendente',
  publishing: 'Publicando',
  published: 'Publicado',
  failed: 'Falhou',
};

export const ACCOUNT_STATUS_LABELS: Record<SocialAccount['status'], string> = {
  manual: 'Cadastro manual',
  connected: 'Conectada',
  expired: 'Token vencido',
  error: 'Com erro',
};

export interface Pillar {
  id: number;
  name: string;
  color: string;
  description?: string;
  target_percent?: number;
  sort_order: number;
  is_active: boolean;
}

export interface SavePillarRequest {
  name: string;
  color: string;
  description?: string | null;
  target_percent?: number | null;
  sort_order?: number;
  is_active?: boolean;
}

export interface PillarBalance {
  pillar_id?: number;
  name: string;
  color: string;
  count: number;
  percent: number;
  target_percent?: number;
  gap?: number;
}

export interface HashtagGroup {
  id: number;
  name: string;
  hashtags: string;
  count: number;
  network?: Network;
  sort_order: number;
  is_active: boolean;
}

export interface SaveHashtagGroupRequest {
  name: string;
  hashtags: string;
  network?: Network | null;
  sort_order?: number;
  is_active?: boolean;
}

export type IdeaKind = 'idea' | 'hook' | 'script' | 'audio';

export const IDEA_KIND_LABELS: Record<IdeaKind, string> = {
  idea: 'Pauta',
  hook: 'Gancho',
  script: 'Roteiro',
  audio: 'Áudio em alta',
};

export interface Idea {
  id: number;
  kind: IdeaKind;
  title: string;
  body?: string;
  network?: Network;
  pillar_id?: number;
  link_url?: string;
  valid_until?: string;
  used_in_post_id?: number;
  is_archived: boolean;
  is_expired: boolean;
  created_at: string;
  created_by: string;
}

export interface SaveIdeaRequest {
  kind: IdeaKind;
  title: string;
  body?: string | null;
  network?: Network | null;
  pillar_id?: number | null;
  link_url?: string | null;
  valid_until?: string | null;
  is_archived?: boolean;
}

export interface FeedItem {
  id: number;
  account_id: number;
  external_id: string;
  media_type?: string;
  caption?: string;
  thumbnail_url?: string;
  permalink?: string;
  posted_at?: string;
  like_count?: number;
  comment_count?: number;
  is_pinned: boolean;
}

export interface SocialGrid {
  network: Network;
  account_id?: number;
  /** Perfil mostrado no cabeçalho (a grade é sempre de um perfil). */
  account?: SocialAccount;
  planned: SocialPost[];
  unscheduled: SocialPost[];
  feed: FeedItem[];
  balance: PillarBalance[];
}

export interface SocialSummary {
  awaiting_approval: number;
  changes_requested: number;
  scheduled_next_7_days: number;
  failed: number;
  my_approvals: number;
}
